import {
  LocalOfferLocation,
  OfferSource,
  VendorItem,
} from '@varo/graphql-schema-types';
import { LocationObject } from 'expo-location';
import { useRouter } from 'expo-router';
import OfferListItem from 'features/rewards/components/OfferListItem/OfferListItem';
import RewardsMap from 'features/rewards/components/RewardsMap/RewardsMap';
import { CATEGORY_ALL_LABEL } from 'features/rewards/constants/constants';
import { mapVendorItemToLocalOfferLocation } from 'features/rewards/helpers/mapVendorItemToLocalOfferLocation';
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import theme from 'shared/styles/theme';

interface NearbyOffersScreenProps {
  categoryId: string;
  categoryName: string;
  offers: VendorItem[];
  loading: boolean;
  error: unknown;
  reload: () => void;
  coords: { latitude: number; longitude: number } | null;
  isLocationFetching: boolean;
  isLocationDenied: boolean;
  initialMapLocation?: google.maps.LatLngLiteral | null;
  initialMapZoom?: number;
  initialMapHeading?: number;
  onMapLocationChange?: (location: google.maps.LatLngLiteral) => void;
  onCameraLocationChange?: (
    location: google.maps.LatLngLiteral,
    zoom?: number,
    heading?: number,
  ) => void;
  selectedVendorId: string | null;
  setSelectedVendorId: React.Dispatch<React.SetStateAction<string | null>>;
}

const NearbyOffers = ({
  categoryId,
  categoryName,
  offers,
  loading,
  coords,
  isLocationFetching,
  isLocationDenied,
  initialMapLocation,
  initialMapZoom,
  initialMapHeading,
  onMapLocationChange,
  onCameraLocationChange,
  selectedVendorId,
  setSelectedVendorId,
}: NearbyOffersScreenProps) => {
  const router = useRouter();
  const lastSelectedVendorRef = useRef<VendorItem | null>(null);
  const [mapLocation, setMapLocation] =
    useState<google.maps.LatLngLiteral | null>(initialMapLocation ?? null);

  const vendorMap = new Map<string, VendorItem>();
  const vendors: LocalOfferLocation[] = [];
  for (const vendor of offers) {
    const mapped = mapVendorItemToLocalOfferLocation(vendor);
    if (mapped) {
      vendors.push(mapped);
      vendorMap.set(mapped.merchantId ?? mapped.id, vendor);
    }
  }

  const resolveSelectedVendor = (): VendorItem | null => {
    if (loading || !selectedVendorId) {
      return null;
    }

    const vendor = vendorMap.get(selectedVendorId);
    if (vendor) {
      return vendor;
    }

    const lastMerchantId = lastSelectedVendorRef.current?.merchantId;
    if (lastMerchantId) {
      return (
        offers.find((vendor) => vendor.merchantId === lastMerchantId) ?? null
      );
    }

    return null;
  };

  const selectedVendor = resolveSelectedVendor();

  useEffect(() => {
    if (selectedVendor) {
      lastSelectedVendorRef.current = selectedVendor;
    }
  }, [selectedVendor]);

  const userCoordinates = coords
    ? { lat: coords.latitude, lng: coords.longitude }
    : undefined;

  const cameraLocation =
    mapLocation ??
    (isLocationFetching || (!coords && !isLocationDenied)
      ? undefined
      : userCoordinates);

  const handleLocationChange = (
    location: google.maps.LatLngLiteral | LocationObject,
    isInitialLoad?: boolean,
  ) => {
    if (!isInitialLoad) {
      const coordinates =
        'coords' in location
          ? { lat: location.coords.latitude, lng: location.coords.longitude }
          : location;
      setMapLocation(coordinates);
      onMapLocationChange?.(coordinates);
    }
  };

  const handleMarkerPress = (point: LocalOfferLocation) => {
    const vendorId = point.merchantId ?? point.id ?? null;
    if (vendorId === selectedVendorId) {
      setSelectedVendorId(null);
    } else {
      setSelectedVendorId(vendorId);
    }
  };

  const resolveNavigationCoords = ():
    { lat: string; lng: string } | undefined => {
    const location = mapLocation ?? userCoordinates;
    if (!location) {
      return undefined;
    }
    return { lat: location.lat.toString(), lng: location.lng.toString() };
  };

  const navigateToUpsideOffer = (
    offerid?: string | null,
    merchantId?: string | null,
    options?: { autoActivate?: boolean },
  ): void => {
    if (!offerid || !merchantId) {
      return;
    }

    const navigationCoords = resolveNavigationCoords();
    const params: {
      offerid: string;
      merchantId: string;
      lat?: string;
      lng?: string;
      autoActivate?: string;
      categoryName?: string;
    } = {
      offerid,
      merchantId,
      ...navigationCoords,
    };

    if (options?.autoActivate) {
      params.autoActivate = 'true';
    }

    if (categoryName && categoryName !== CATEGORY_ALL_LABEL) {
      params.categoryName = categoryName;
    }

    router.push({ pathname: '/rewards/upside-offers/[merchantId]', params });
  };

  const navigateToKardOffer = (): void => {
    if (!selectedVendor?.id) {
      return;
    }

    const navigationCoords = resolveNavigationCoords();
    const params: {
      id: string;
      lat?: string;
      lng?: string;
    } = {
      id: selectedVendor.id,
      ...navigationCoords,
    };

    router.push({ pathname: '/rewards/[id]', params });
  };

  const handleOnPress = () => {
    if (selectedVendor?.offerSource === OfferSource.Upside) {
      navigateToUpsideOffer(selectedVendor?.id, selectedVendor?.merchantId);
    } else {
      navigateToKardOffer();
    }
  };

  const handleActivateOffer = () => {
    if (selectedVendor) {
      navigateToUpsideOffer(selectedVendor.id, selectedVendor.merchantId, {
        autoActivate: true,
      });
    }
  };

  return (
    <GestureHandlerRootView style={styles.container}>
      <View style={styles.mapWrapper}>
        {cameraLocation ? (
          <RewardsMap
            id="nearbyOffersMap"
            sectionTitle=""
            markerData={vendors}
            cameraLocation={cameraLocation}
            onMarkerPress={handleMarkerPress}
            onLocationChange={handleLocationChange}
            shouldShowSearchThisAreaButtonVisible
            isLoading={loading}
            selectedMarkerId={selectedVendorId}
            onCameraLocationChange={onCameraLocationChange}
            defaultCameraZoom={initialMapZoom}
            defaultCameraHeading={initialMapHeading}
            onMapTap={() => {
              setSelectedVendorId(null);
            }}
          />
        ) : null}
      </View>

      {selectedVendor && (
        <View style={styles.nearbyCountContainer}>
          <View style={styles.offerDetailsContainer}>
            <OfferListItem
              id={selectedVendor.id}
              titleText={selectedVendor.name ?? ''}
              subtitleText={selectedVendor.cashbackRateFormatted ?? ''}
              subtitleTextSecondary={
                selectedVendor.offerSource === OfferSource.Upside
                  ? (selectedVendor.tertiary ?? undefined)
                  : undefined
              }
              distance={selectedVendor.distanceFormatted ?? undefined}
              originalPrice={
                selectedVendor.pricingTier?.originalPrice?.formatted
              }
              discountedPrice={
                selectedVendor.pricingTier?.disountedPrice?.formatted
              }
              address={selectedVendor.address?.addressLine1 ?? ''}
              iconUrl={selectedVendor.logoUrl ?? undefined}
              onPress={handleOnPress}
              analyticsId="nearbyOfferMapCardSelected"
              customAttributes={{
                offer_id: selectedVendor.id,
                merchant_name: selectedVendor.name,
                category: categoryId,
              }}
              isDisabled={false}
              activateOfferAction={
                selectedVendor.offerSource === OfferSource.Upside &&
                !selectedVendor.isActive
                  ? handleActivateOffer
                  : undefined
              }
              activateOfferAnalyticsId="offer-activate-clicked"
              activateOfferCustomAttributes={{
                offer_id: selectedVendor.id,
                merchant_id: selectedVendor.merchantId,
                merchant_name: selectedVendor.name,
                offer_bracket: categoryName ?? '',
              }}
              tagText={selectedVendor?.tag?.text}
            />
          </View>
        </View>
      )}
    </GestureHandlerRootView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  mapWrapper: {
    flex: 1,
    width: '100%',
    paddingTop: 56, // pill group container height
  },
  nearbyCountContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  offerDetailsContainer: {
    backgroundColor: theme.color.background.primary,
    borderRadius: 4,
    boxShadow:
      '0 4px 36px 0 rgba(0, 0, 0, 0.16), 0 2px 8px 0 rgba(0, 0, 0, 0.12)',
    marginHorizontal: theme.spacing.m,
    marginBottom: theme.spacing.xxl,
    overflow: 'hidden',
  },
});

export default NearbyOffers;
